/**
 * Die JTL-Suche - Global Constraint 16 (Roadmap, woertlich): "Suchfelder gegen JTL: Debounce 300 ms,
 * Mindestlaenge 2 Zeichen, serverseitig auf Top-50 Treffer begrenzt, LRU-Cache 60 s je normalisiertem
 * Suchbegriff, Single-Flight-Guard."
 *
 * ================= EINE FASSUNG - AUS ZWEI KOPIEN ZUSAMMENGEFUEHRT =================
 *
 * Diese Datei entstand zweimal unabhaengig (add/add): auf `feat/09-react-spa` in Task 23 und auf
 * `feat/09-spa-store` in Task 25. Beim Merge der beiden Zweige war der Codeteil ab
 * `export interface JtlSucheOptionen` **bytegleich** (110 Zeilen) - die von Task 25 geforderte
 * Probe ("sie muessen bytegleich sein, sonst gab es zwischenzeitlich eine Abweichung") ist damit
 * bestanden, es gab keine Abweichung. Aufgeloest wurde folglich nur der Kopfkommentar, als
 * Vereinigung beider Begruendungen; der Rumpf ist unveraendert.
 *
 * ================= VORGEZOGEN AUS TASK 17 - BEGRUENDUNG (Task 23) =================
 *
 * Laut Phasenplan (`docs/superpowers/plans/2026-08-09-phase-06-react-spa.md`, Abschnitt "Typkonsistenz")
 * entsteht `jtlSuche` EINMAL in Task 17 ("Drawer-Tab „Bestellung"") und wird danach in Task 23
 * (diese Datei) und Task 25 wiederverwendet. Task 17 ist in diesem Zweig zum Zeitpunkt von Task 23
 * NOCH NICHT umgesetzt (kein `task-17-brief.md`/`task-17-report.md` in `.superpowers/sdd/...`, keine
 * `src/api/jtlSuche.ts` im Baum) - Tasks werden hier ausserhalb der Plan-Reihenfolge abgearbeitet.
 *
 * Diese Datei liefert deshalb VORGEZOGEN genau das, was der Phasenplan als Schnittstelle von Task 17
 * beschreibt (Konstruktor `jtlSuche({ abruf })`, Rueckgabe `(begriff: string) => Promise<T[]>`,
 * dieselben vier CLIENT-SEITIGEN Eigenschaften aus Constraint 16). Task 17 soll diese Datei
 * WIEDERVERWENDEN, nicht neu anlegen - sonst entsteht dieselbe add/add-Kollision wie bei `Ikone.tsx`
 * (siehe dortiger Kopfkommentar).
 *
 * ================= WAS HIER NICHT GEBAUT WIRD =================
 *
 * Das FUENFTE Merkmal aus Constraint 16 - "serverseitig auf Top-50 Treffer begrenzt" - ist eine
 * BACKEND-Eigenschaft des tatsaechlichen JTL-Such-Endpunkts. Dieser Endpunkt existiert in Phase 4a
 * NICHT: der dortige Plan (`docs/superpowers/plans/2026-08-09-phase-04a-domaenen-api.md`) kennt nur
 * `GET /api/suppliers/:kLieferant` (Einzelabruf nach JTL-Kennung), keine Artikel- oder
 * Lieferanten-SUCHE. Diese Datei erfindet deshalb KEINEN Pfad wie `/api/jtl/suppliers/search` -
 * das waere Erfindung statt Uebernahme. `jtlSuche()` ist bewusst generisch (nimmt `abruf` als
 * Argument entgegen) und kennt keinen konkreten Endpunkt; die tatsaechliche Anbindung ist ein
 * Einhaengepunkt fuer eine spaetere Phase (siehe Taskbericht, Abschnitt "Einhaengepunkte").
 *
 * ================= NUTZUNG DURCH TASK 25 (`feat/09-spa-store`) =================
 *
 * Task 25 (`components/Shipments/`) brauchte dieselbe Funktion, hatte Task 23 aber nicht im Baum
 * und legte deshalb eine woertliche Kopie an. Fuer die Sendungssuche reicht dort
 * `suche: (begriff: string) => Promise<Sendung[]>` als Prop; die Anbindung an einen echten
 * Sendungssuche-Endpunkt ist ebenso ein Einhaengepunkt wie die JTL-Anbindung oben.
 *
 * Weitere Kopien koennen noch auf `feat/09-spa-seiten` (Task 17) und `feat/09-spa-ek` liegen -
 * beim naechsten Merge gilt dieselbe Probe: erst Bytegleichheit des Rumpfs pruefen, dann die
 * Kopfkommentare vereinigen, NICHT eine Seite nehmen.
 *
 * ================= MERGE `feat/09-spa-seiten`: DIE PROBE IST DURCHGEFALLEN ==================
 *
 * Task 17 hatte auf `feat/09-spa-seiten` unter DIESEM Pfad eine eigene, **fachlich reichere**
 * Fassung angelegt. Die oben verlangte Bytegleichheitsprobe des Rumpfs ist NICHT bestanden: die
 * beiden Fassungen sind nicht dieselbe Funktion mit zwei Kopfkommentaren, sondern zwei verschiedene
 * Module unter einem Namen. Unterschiede, jeder einzeln beobachtbar:
 *
 * | Merkmal | diese Fassung (Task 23/25) | Task 17 |
 * | --- | --- | --- |
 * | Optionsnamen | `debounceMs`, `mindestlaenge`, `cacheMs`, `maxEintraege` | `verzoegerungMs`, `mindestLaenge`, `cacheDauerMs`, `cacheGroesse`, `hoechstzahl`, `jetzt` |
 * | Top-50 (5. Merkmal Constraint 16) | **nicht gebaut** (siehe oben) | `grenze` reist zum Abruf UND die Antwort wird beschnitten |
 * | Cacheinhalt | die `Promise` | die aufgeloeste Trefferliste, mit Erfassungszeitpunkt |
 * | Cache-Treffer | laeuft weiter durch den Debounce | antwortet SOFORT, ohne Debounce |
 * | zu kurzer Begriff | liefert `[]`, laufender Debounce bleibt stehen | liefert `[]` UND verwirft den laufenden Debounce |
 * | Uhr | fest `Date.now` | einhaengbar (`jetzt`) |
 * | Normalisierung | modulinternes `normalisieren` (auch `\s+` -> `" "`) | `normSearch` aus `lib/format.ts` (portiert) |
 * | benannte Konstanten | keine | `JTL_SUCHE_*`, je einzeln exportiert und geprueft |
 *
 * Eine Vereinigung in EIN Modul waere keine Konfliktauflösung, sondern eine Umsetzung: sie muesste
 * sechs Verhaltensfestlegungen gegeneinander entscheiden, und jede Entscheidung wuerde Zusicherungen
 * der jeweils anderen Seite fallen lassen (beide Fassungen haben eine eigene, gruene Testdatei -
 * 6 Faelle hier, 8 dort). Der Merge trennt deshalb die Pfade, statt Abdeckung zu verlieren:
 *
 * - **diese Datei** bleibt `api/jtlSuche.ts` (Verbraucher `NewOrder/JtlSuchfeld.tsx`,
 *   `Shipments/SendungsSuche.tsx`; Test `test/komponenten/api/jtlSuche.test.ts`)
 * - **Task 17s Fassung** liegt jetzt unter `api/jtlArtikelSuche.ts` (Verbraucher
 *   `Drawer/MainTab/Positionen.tsx` + `typen.ts`; Test `test/komponenten/api/jtlArtikelSuche.test.ts`)
 *
 * ⚠ OFFENE ENTSCHEIDUNG, nicht vom Merge getroffen: mittelfristig soll es EINE Suche geben, und
 * Task 17s Fassung ist die vollstaendigere (sie ist die einzige, die das fuenfte Merkmal aus
 * Constraint 16 ueberhaupt baut). Die Zusammenfuehrung heisst: die zwei Verbraucher dieser Datei auf
 * `jtlArtikelSuche` umstellen und die 6 Faelle hier gegen die 8 dort abgleichen. Das ist eine
 * fachliche Aenderung mit Verhaltensfolgen (Top-50-Beschnitt, Cache-Treffer ohne Debounce) und
 * gehoert in einen eigenen Task, nicht in einen Merge.
 */

export interface JtlSucheOptionen<T> {
  /**
   * Der eigentliche Serverabruf fuer EINEN (bereits normalisierten) Suchbegriff. Der optionale
   * zweite Parameter `kontext` (z. B. eine Lieferanten-Kennung) geht NICHT in den Suchbegriff
   * selbst ein, sondern haengt getrennt daneben - siehe `suchen()` unten, warum Cache und
   * Single-Flight-Guard trotzdem danach unterscheiden muessen.
   */
  abruf: (begriff: string, kontext?: string) => Promise<T[]>;
  /** Default 300 ms - Constraint 16. */
  debounceMs?: number;
  /** Default 2 Zeichen - Constraint 16. */
  mindestlaenge?: number;
  /** Default 60_000 ms - Constraint 16 ("LRU-Cache 60 s"). */
  cacheMs?: number;
  /**
   * Obergrenze der gleichzeitig im Cache gehaltenen Suchbegriffe (LRU-Verdraengung). Keine
   * eigene Konstraint-Vorgabe - eine defensive Ergaenzung gegen unbeschraenktes Wachstum bei
   * vielen verschiedenen Suchbegriffen in einer langen Sitzung. Default 50.
   */
  maxEintraege?: number;
}

interface CacheEintrag<T> {
  zeit: number;
  ergebnis: Promise<T[]>;
}

/** Default 2 Zeichen - Constraint 16. Exportiert, damit Suchfelder "zu kurz" anzeigen koennen,
 *  ohne die Zahl abzuschreiben. */
export const JTL_SUCHE_MINDESTLAENGE = 2;

/** `"  ROHR  "` und `"rohr"` sind derselbe Suchbegriff - Voraussetzung fuer den Cache-Treffer.
 *  Exportiert aus demselben Grund wie die Mindestlaenge: wer die Laenge eines Begriffs gegen die
 *  Mindestlaenge haelt, muss ihn vorher GENAUSO normalisieren wie diese Datei. */
export function normalisiereSuchbegriff(begriff: string): string {
  return String(begriff ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();
}

/**
 * Baut eine Suchfunktion mit Debounce, Mindestlaenge, 60s-Cache und Single-Flight-Guard um `abruf`.
 * Eine Instanz haelt ihren Zustand (Cache, laufende Anfragen, Debounce-Timer) in ihrem eigenen
 * Closure - je Suchfeld (bzw. je Testfall) wird `jtlSuche(...)` deshalb genau EINMAL aufgerufen.
 * Gecacht wird nur ein gelungener Abruf; eine Ablehnung entfernt ihren Eintrag wieder.
 *
 * Die zurueckgegebene Funktion nimmt optional einen zweiten Parameter `kontext` entgegen (z. B.
 * eine Lieferanten-Kennung). Cache und Single-Flight-Guard schluesseln intern ueber
 * `` `${kontext ?? "-"}:${begriff}` `` - derselbe Begriff mit verschiedenem Kontext ist damit ein
 * EIGENER Cache-Eintrag (keine Kollision), waehrend `abruf` selbst weiterhin die beiden reinen
 * Werte getrennt bekommt, nicht den zusammengesetzten Schluessel. Aufrufer ohne Kontext (der
 * ueberwiegende Bestand) sind unveraendert: `kontext` bleibt `undefined`, der Schluessel reduziert
 * sich auf `"-:${begriff}"`, und `abruf` wird weiterhin nur mit dem Begriff aufgerufen (kein
 * zusaetzliches `undefined`-Argument) - das haelt bestehende Erwartungen an `abruf`-Aufrufe intakt.
 */
export function jtlSuche<T>(
  optionen: JtlSucheOptionen<T>,
): (begriff: string, kontext?: string) => Promise<T[]> {
  const {
    abruf,
    debounceMs = 300,
    mindestlaenge = JTL_SUCHE_MINDESTLAENGE,
    cacheMs = 60_000,
    maxEintraege = 50,
  } = optionen;

  const cache = new Map<string, CacheEintrag<T>>();
  const laufend = new Map<string, Promise<T[]>>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let wartende: Array<{
    begriff: string;
    kontext: string | undefined;
    resolve: (wert: T[]) => void;
    reject: (fehler: unknown) => void;
  }> = [];

  /** Zusammengesetzter Cache-/Single-Flight-Schluessel - NUR intern, `abruf` sieht ihn nie. */
  function schluesselVon(begriff: string, kontext: string | undefined): string {
    return `${kontext ?? "-"}:${begriff}`;
  }

  function ausMemory(schluessel: string): Promise<T[]> | undefined {
    const eintrag = cache.get(schluessel);
    if (!eintrag) return undefined;
    if (Date.now() - eintrag.zeit >= cacheMs) {
      cache.delete(schluessel);
      return undefined;
    }
    // LRU: ein Treffer ruecht den Eintrag ans Ende (juengste Nutzung) - Map behaelt Einfuegereihenfolge.
    cache.delete(schluessel);
    cache.set(schluessel, eintrag);
    return eintrag.ergebnis;
  }

  /** Fuehrt die Suche fuer GENAU EINEN normalisierten Begriff (+ Kontext) aus - Cache- und Single-Flight-Guard. */
  function ausfuehren(begriff: string, kontext: string | undefined): Promise<T[]> {
    const schluessel = schluesselVon(begriff, kontext);
    const gecached = ausMemory(schluessel);
    if (gecached) return gecached;

    const bereitsLaufend = laufend.get(schluessel);
    if (bereitsLaufend) return bereitsLaufend;

    // Kontext nur mitgeben, wenn er auch da ist - sonst bekaeme `abruf` ein zusaetzliches
    // `undefined`-Argument, das bestehende Aufrufer/Erwartungen nicht kennen.
    // Eine Ablehnung (ApiFehler, VerbindungsFehler, Abbruch, jeder andere Fehler) bleibt NICHT im Cache:
    // sonst lieferte derselbe Begriff nach der Rueckkehr des Netzes bis zu 60 s lang weiter die alte
    // Ablehnung. Der Eintrag faellt VOR der Weitergabe des Fehlers, also bevor ein Aufrufer neu sucht.
    const versprechen: Promise<T[]> = (
      kontext === undefined ? abruf(begriff) : abruf(begriff, kontext)
    )
      .catch((fehler: unknown) => {
        if (cache.get(schluessel)?.ergebnis === versprechen) cache.delete(schluessel);
        throw fehler;
      })
      .finally(() => laufend.delete(schluessel));
    laufend.set(schluessel, versprechen);
    cache.set(schluessel, { zeit: Date.now(), ergebnis: versprechen });
    while (cache.size > maxEintraege) {
      const aeltesterSchluessel = cache.keys().next().value;
      if (aeltesterSchluessel === undefined) break;
      cache.delete(aeltesterSchluessel);
    }
    return versprechen;
  }

  return function suche(rohBegriff: string, kontext?: string): Promise<T[]> {
    const begriff = normalisiereSuchbegriff(rohBegriff);
    if (begriff.length < mindestlaenge) {
      // Mindestlaenge: kein Debounce, keine Anfrage - sofort ein leeres Ergebnis.
      return Promise.resolve([]);
    }
    return new Promise<T[]>((resolve, reject) => {
      wartende.push({ begriff, kontext, resolve, reject });
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const anstehende = wartende;
        wartende = [];
        // Debounce (trailing edge): nur der ZULETZT eingegangene Begriff (samt seinem Kontext) wird
        // tatsaechlich gesucht. Aufrufe, die im selben Zeitfenster ueberholt wurden, bekommen
        // dasselbe Ergebnis - das entspricht demselben Verhalten wie ein einzelnes, spaeter
        // ausgeloestes Suchfeld.
        const letzter = anstehende[anstehende.length - 1]!;
        ausfuehren(letzter.begriff, letzter.kontext).then(
          (ergebnis) => anstehende.forEach((w) => w.resolve(ergebnis)),
          (fehler) => anstehende.forEach((w) => w.reject(fehler)),
        );
      }, debounceMs);
    });
  };
}
